import { getSupportContactLink } from '@psyomics/ui-common';
import { useLayoutEffect } from 'react';
import { Trans } from 'react-i18next';

import { BackgroundVariant, useBackground } from '../../../background';
import { cn } from '../../../shadcn/lib/utils';
import { Header } from '../../atoms/header/header';
import { Illustration, IllustrationName } from '../../atoms/illustration/illustration';
import { Text } from '../../atoms/text/text';

type GenericErrorProps = {
  readonly className?: string;
  readonly titleKey?: string;
  readonly subtitleKey?: string;
  readonly titleComponents?: Record<string, React.ReactElement>;
  readonly illustration?: IllustrationName;
  readonly backgroundVariant?: BackgroundVariant;
  readonly ellipse1Variant?: BackgroundVariant;
  readonly ellipse2Variant?: BackgroundVariant;
  readonly contact?: string;
};

const DEFAULT_TITLE_KEY = 'ui-components.genericError.title';
const DEFAULT_SUBTITLE_KEY = 'ui-components.genericError.subtitle';

function GenericError({
  className,
  titleKey = DEFAULT_TITLE_KEY,
  titleComponents,
  subtitleKey = DEFAULT_SUBTITLE_KEY,
  illustration = 'SoftwareTester',
  backgroundVariant,
  ellipse1Variant,
  ellipse2Variant,
  contact,
}: GenericErrorProps) {
  const { setBackground, resetBackground } = useBackground();
  const { supportContact, supportHref } = getSupportContactLink(contact);

  useLayoutEffect(() => {
    if (!backgroundVariant && !ellipse1Variant && !ellipse2Variant) return;
    setBackground({ visible: true, variant: backgroundVariant, ellipse1Variant, ellipse2Variant });
    return () => {
      resetBackground();
    };
  }, [backgroundVariant, ellipse1Variant, ellipse2Variant, resetBackground, setBackground]);

  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-[414px] min-h-screen flex-col items-center justify-center gap-4 text-center',
        className
      )}
    >
      <Header className="text-center" level="h1">
        <Trans components={titleComponents} i18nKey={titleKey} />
      </Header>
      <Text className={'text-center'} size="lg" tag="p">
        <Trans
          components={{
            linkTag: (
              <a className="text-blue-500 underline" href={supportHref}>
                {supportContact}
              </a>
            ),
          }}
          i18nKey={subtitleKey}
        />
      </Text>
      <Illustration height={300} name={illustration} width={300} />
    </div>
  );
}

export { GenericError, type GenericErrorProps };
